import { useEffect, useMemo, useRef } from 'react';
import { Animated as RNAnimated, Platform, StyleSheet, Text, useWindowDimensions, View } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import Animated, { runOnJS, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import Svg, { Polygon } from 'react-native-svg';

import { useTheme } from '@/contexts/ThemeContext';
import { GREECE_MAP_VIEWBOX, GREECE_NOMOI } from '@/lib/greeceNomoiMap';

type Props = {
  countsByRegion: Record<string, number>;
  /** Προαιρετικό — αριθμός ανοιχτών αιτημάτων ανά νομό (πράσινες φούσκες, στυλ
   * Taskbro "Ζητάνε"). Αν παραλειφθεί, δείχνονται μόνο οι μπλε φούσκες επαγγελματιών. */
  requestCountsByRegion?: Record<string, number>;
  selectedRegion: string | null;
  onSelectRegion: (region: string | null) => void;
  /** Προαιρετική σταθερή πλάτη — π.χ. για μικρή προεπισκόπηση (peek), αντί για τον
   * αυτόματο υπολογισμό από το πλάτος της οθόνης. */
  width?: number;
};

const [VIEWBOX_W, VIEWBOX_H] = GREECE_MAP_VIEWBOX.split(' ').slice(2).map(Number);

const EMPTY_COLOR = '#64748B';   // γκρι — χωρίς επαγγελματίες
const HAS_PROS_COLOR = '#3B82F6'; // μπλε — έχει επαγγελματίες ("Προσφέρουν")
const HAS_REQUESTS_COLOR = '#22C55E'; // πράσινο — υπάρχουν ανοιχτά αιτήματα ("Ζητάνε")
const SELECTED_COLOR = '#C9A843'; // χρυσό — επιλεγμένος νομός

const MAX_SCALE = 4;

function parsePoints(points: string): [number, number][] {
  return points
    .trim()
    .split(/\s+/)
    .map((pair) => {
      const [x, y] = pair.split(',').map(Number);
      return [x, y] as [number, number];
    });
}

/** Κέντρο βάρους ενός polygon (μέσος όρος σημείων) — αρκετά καλή προσέγγιση
 * για την τοποθέτηση της αριθμημένης φούσκας, χωρίς την πολυπλοκότητα ενός
 * πραγματικού υπολογισμού centroid-by-area. */
function polygonCentroid(poly: [number, number][]): [number, number] {
  const n = poly.length;
  const sum = poly.reduce((acc, [x, y]) => [acc[0] + x, acc[1] + y] as [number, number], [0, 0] as [number, number]);
  return [sum[0] / n, sum[1] / n];
}

/** Ray-casting point-in-polygon — πιο αξιόπιστο από per-shape onPress σε SVG στο Android. */
function pointInPolygon(x: number, y: number, poly: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    const intersects = (yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

type RequestBadgeProps = {
  size: number;
  left: number;
  top: number;
  count: number;
  selected: boolean;
};

/** Η πράσινη φούσκα αιτημάτων κάνει "pop" (scale+fade out) μόλις επιλεγεί ο
 * νομός της — οπτικό feedback ότι «μαζεύτηκε» η ένδειξη γιατί μόλις είδες
 * τα αιτήματα αυτού του νομού. */
function RequestBadge({ size, left, top, count, selected }: RequestBadgeProps) {
  const anim = useRef(new RNAnimated.Value(1)).current;

  useEffect(() => {
    RNAnimated.timing(anim, {
      toValue: selected ? 0 : 1,
      duration: selected ? 220 : 0,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  }, [selected, anim]);

  return (
    <RNAnimated.View
      pointerEvents="none"
      style={[
        styles.badge,
        {
          left: left - size / 2,
          top: top - size / 2,
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: HAS_REQUESTS_COLOR,
          opacity: anim,
          transform: [{ scale: anim }],
        },
      ]}
    >
      <Text style={styles.badgeText} numberOfLines={1} adjustsFontSizeToFit>
        {count > 999 ? '999+' : count}
      </Text>
    </RNAnimated.View>
  );
}

/** Διαδραστικός, απλοποιημένος χάρτης Ελλάδας ανά νομό — πάτημα φιλτράρει live,
 * και υποστηρίζει pinch-to-zoom + μεταφορά με δύο δάχτυλα. */
export function GreeceMap({ countsByRegion, requestCountsByRegion, selectedRegion, onSelectRegion, width }: Props) {
  const { colors } = useTheme();
  const { width: screenWidth } = useWindowDimensions();
  const mapWidth = width ?? Math.min(420, screenWidth - 32);
  // Το ύψος υπολογίζεται από την ακριβή αναλογία του viewBox — αν δεν ταίριαζε
  // ακριβώς, το SVG πρόσθετε αόρατο περιθώριο (letterboxing) στα πλάγια, που
  // δεν λαμβανόταν υπόψη στον μετασχηματισμό συντεταγμένων του πατήματος και
  // έκανε αδύνατο το πάτημα σε σχήματα μακριά από το κέντρο (π.χ. νησιά).
  const mapHeight = mapWidth * (VIEWBOX_H / VIEWBOX_W);

  const scale = useSharedValue(1);
  const savedScale = useSharedValue(1);
  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);
  const savedTranslateX = useSharedValue(0);
  const savedTranslateY = useSharedValue(0);

  function clampTranslate(tx: number, ty: number, s: number) {
    'worklet';
    const maxX = (mapWidth * (s - 1)) / 2;
    const maxY = (mapHeight * (s - 1)) / 2;
    return {
      x: Math.max(-maxX, Math.min(maxX, tx)),
      y: Math.max(-maxY, Math.min(maxY, ty)),
    };
  }

  const parsedNomoi = useMemo(
    () => GREECE_NOMOI.map((n) => ({ region: n.region, polys: n.shapes.map(parsePoints) })),
    [],
  );

  // Κέντρο του μεγαλύτερου (κυρίου) polygon κάθε νομού — για νησιωτικούς
  // νομούς με πολλά μικρά shapes, το κύριο νησί δίνει καλύτερη τοποθέτηση
  // από τον μέσο όρο όλων.
  const regionCentroids = useMemo(() => {
    const map: Record<string, [number, number]> = {};
    for (const { region, polys } of parsedNomoi) {
      const largest = polys.reduce((a, b) => (b.length > a.length ? b : a), polys[0]);
      if (largest) map[region] = polygonCentroid(largest);
    }
    return map;
  }, [parsedNomoi]);

  // Bounding box ανά νομό — χρησιμοποιείται για το "γενναιόδωρο" tap-fallback
  // στα νησιά (βλ. παρακάτω): μικρά, απομονωμένα σχήματα παίρνουν ένα
  // αόρατο περιθώριο πάνω στη θάλασσα γύρω τους, ώστε να μην χρειάζεται
  // ακριβοσκόπευτο tap πάνω στο μικρό νησάκι.
  const regionBounds = useMemo(() => {
    const map: Record<string, { minX: number; maxX: number; minY: number; maxY: number; area: number }> = {};
    for (const { region, polys } of parsedNomoi) {
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      for (const poly of polys) {
        for (const [x, y] of poly) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
      map[region] = { minX, maxX, minY, maxY, area: (maxX - minX) * (maxY - minY) };
    }
    return map;
  }, [parsedNomoi]);

  // "Νησιωτικός" νομός = μικρό bounding box σε σχέση με όλο τον χάρτη — αυτά
  // παίρνουν το γενναιόδωρο sea-tap fallback, όχι οι μεγάλες ηπειρωτικές
  // περιοχές (θα μπέρδευε ποιος νομός εννοείται σε πυκνές περιοχές).
  const ISLAND_AREA_RATIO = 0.02;
  const SEA_TAP_MARGIN = 22; // viewbox units γύρω από το bounding box
  const totalMapArea = VIEWBOX_W * VIEWBOX_H;

  function handleTapAt(x: number, y: number) {
    const vx = (x / mapWidth) * VIEWBOX_W;
    const vy = (y / mapHeight) * VIEWBOX_H;
    const hit = parsedNomoi.find(({ polys }) => polys.some((poly) => pointInPolygon(vx, vy, poly)));
    if (hit) {
      onSelectRegion(selectedRegion === hit.region ? null : hit.region);
      return;
    }

    // Fallback: αν το tap έπεσε στη θάλασσα κοντά σε κάποιο μικρό/νησιωτικό
    // νομό, επίλεξέ τον — διάλεξε τον πλησιέστερο αν ταιριάζουν παραπάνω από ένας.
    let closest: { region: string; dist: number } | null = null;
    for (const [region, b] of Object.entries(regionBounds)) {
      if (b.area / totalMapArea > ISLAND_AREA_RATIO) continue;
      const dx = Math.max(b.minX - vx, 0, vx - b.maxX);
      const dy = Math.max(b.minY - vy, 0, vy - b.maxY);
      const dist = Math.hypot(dx, dy);
      if (dist <= SEA_TAP_MARGIN && (!closest || dist < closest.dist)) {
        closest = { region, dist };
      }
    }
    if (closest) onSelectRegion(selectedRegion === closest.region ? null : closest.region);
  }

  const tap = Gesture.Tap()
    .maxDuration(250)
    .onEnd((e) => {
      runOnJS(handleTapAt)(e.x, e.y);
    });

  const pinch = Gesture.Pinch()
    .onUpdate((e) => {
      const next = Math.max(1, Math.min(savedScale.value * e.scale, MAX_SCALE));
      scale.value = next;
      const clamped = clampTranslate(translateX.value, translateY.value, next);
      translateX.value = clamped.x;
      translateY.value = clamped.y;
    })
    .onEnd(() => {
      savedScale.value = scale.value;
      savedTranslateX.value = translateX.value;
      savedTranslateY.value = translateY.value;
      if (scale.value <= 1) {
        scale.value = withTiming(1);
        savedScale.value = 1;
        translateX.value = withTiming(0);
        translateY.value = withTiming(0);
        savedTranslateX.value = 0;
        savedTranslateY.value = 0;
      }
    });

  // Ένα δάχτυλο αρκεί για μεταφορά — αλλά μόνο όταν είναι ήδη ζουμαρισμένος ο
  // χάρτης, ώστε να μη μπερδεύεται με το tap-to-select στο κανονικό zoom.
  // Το activeOffset αφήνει μικρά taps να περνάνε ανενόχλητα στο tap gesture.
  const pan = Gesture.Pan()
    .minPointers(1)
    .maxPointers(2)
    .activeOffsetX([-8, 8])
    .activeOffsetY([-8, 8])
    .onUpdate((e) => {
      if (savedScale.value <= 1) return;
      const clamped = clampTranslate(savedTranslateX.value + e.translationX, savedTranslateY.value + e.translationY, scale.value);
      translateX.value = clamped.x;
      translateY.value = clamped.y;
    })
    .onEnd(() => {
      savedTranslateX.value = translateX.value;
      savedTranslateY.value = translateY.value;
    });

  const doubleTap = Gesture.Tap()
    .numberOfTaps(2)
    .onEnd(() => {
      if (savedScale.value > 1) {
        scale.value = withTiming(1);
        savedScale.value = 1;
        translateX.value = withTiming(0);
        translateY.value = withTiming(0);
        savedTranslateX.value = 0;
        savedTranslateY.value = 0;
      } else {
        scale.value = withTiming(2);
        savedScale.value = 2;
      }
    });

  const composed = Gesture.Simultaneous(pinch, pan, Gesture.Exclusive(doubleTap, tap));

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [
      { translateX: translateX.value },
      { translateY: translateY.value },
      { scale: scale.value },
    ],
  }));

  return (
    <View style={[styles.wrap, { width: mapWidth, height: mapHeight }]}>
      <GestureDetector gesture={composed}>
        <Animated.View style={animatedStyle}>
          <Svg viewBox={GREECE_MAP_VIEWBOX} width={mapWidth} height={mapHeight}>
            {GREECE_NOMOI.map(({ region, shapes }) => {
              const count = countsByRegion[region] ?? 0;
              const empty = count === 0;
              const selected = selectedRegion === region;
              const fill = selected ? SELECTED_COLOR : empty ? EMPTY_COLOR : HAS_PROS_COLOR;
              const fillOpacity = empty ? 0.35 : selected ? 1 : 0.75;
              const stroke = selected ? SELECTED_COLOR : colors.background;

              return shapes.map((points, i) => (
                <Polygon
                  key={`${region}-${i}`}
                  points={points}
                  fill={fill}
                  fillOpacity={fillOpacity}
                  stroke={stroke}
                  strokeWidth={selected ? 2 : 0.5}
                />
              ));
            })}
          </Svg>

          {/* Αριθμημένες φούσκες πάνω στον χάρτη (στυλ Taskbro) — δείχνουν πόσοι
              επαγγελματίες/αιτήματα υπάρχουν σε κάθε νομό με μια ματιά, χωρίς να
              χρειάζεται tap. Pointer events off ώστε το tap να περνάει στο SVG από κάτω. */}
          <View style={StyleSheet.absoluteFillObject} pointerEvents="none">
            {GREECE_NOMOI.map(({ region }) => {
              const count = countsByRegion[region] ?? 0;
              const requestCount = requestCountsByRegion?.[region] ?? 0;
              if (count === 0 && requestCount === 0) return null;
              const centroid = regionCentroids[region];
              if (!centroid) return null;
              const selected = selectedRegion === region;
              const px = (centroid[0] / VIEWBOX_W) * mapWidth;
              const py = (centroid[1] / VIEWBOX_H) * mapHeight;
              const hasBoth = count > 0 && requestCount > 0;

              return (
                <View key={region}>
                  {count > 0 && (() => {
                    const size = count >= 100 ? 30 : count >= 10 ? 26 : 22;
                    const offsetX = hasBoth ? -size * 0.55 : 0;
                    return (
                      <View
                        style={[
                          styles.badge,
                          {
                            left: px - size / 2 + offsetX,
                            top: py - size / 2,
                            width: size,
                            height: size,
                            borderRadius: size / 2,
                            backgroundColor: selected ? SELECTED_COLOR : HAS_PROS_COLOR,
                          },
                        ]}
                      >
                        <Text style={styles.badgeText} numberOfLines={1} adjustsFontSizeToFit>
                          {count > 999 ? '999+' : count}
                        </Text>
                      </View>
                    );
                  })()}
                  {requestCount > 0 && (() => {
                    const size = requestCount >= 100 ? 30 : requestCount >= 10 ? 26 : 22;
                    const offsetX = hasBoth ? size * 0.55 : 0;
                    return (
                      <RequestBadge
                        size={size}
                        left={px + offsetX}
                        top={py}
                        count={requestCount}
                        selected={selected}
                      />
                    );
                  })()}
                </View>
              );
            })}
          </View>
        </Animated.View>
      </GestureDetector>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  badge: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: '#fff',
    shadowColor: '#000',
    shadowOpacity: 0.3,
    shadowRadius: 3,
    shadowOffset: { width: 0, height: 1 },
    elevation: 3,
  },
  badgeText: {
    color: '#fff',
    fontSize: 11,
    fontWeight: '800',
  },
});
